import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import './BlogPostDetail.css';

interface BlogBlock {
  type:    string;
  html:    string;
  url:     string;
  caption: string;
}

interface BlogPost {
  id:            string;
  title:         string;
  slug:          string;
  category:      string;
  services:      string[];
  location:      string;
  clientType:    string;
  summary:       string;
  coverImageUrl: string;
  publishDate:   string | null;
  author:        string;
  featured:      boolean;
  tags:          string[];
  body:          BlogBlock[];
}

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:5000';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
}

// ── Block renderer ────────────────────────────────────────────────────
function renderBlocks(blocks: BlogBlock[]): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let i = 0;

  while (i < blocks.length) {
    const block = blocks[i];

    // Group consecutive bullets into a <ul>
    if (block.type === 'bullet') {
      const items: BlogBlock[] = [];
      while (i < blocks.length && blocks[i].type === 'bullet') {
        items.push(blocks[i++]);
      }
      nodes.push(
        <ul key={`ul-${i}`} className="post-list">
          {items.map((b, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: b.html }} />
          ))}
        </ul>
      );
      continue;
    }

    // Group consecutive numbered items into a <ol>
    if (block.type === 'numbered') {
      const items: BlogBlock[] = [];
      while (i < blocks.length && blocks[i].type === 'numbered') {
        items.push(blocks[i++]);
      }
      nodes.push(
        <ol key={`ol-${i}`} className="post-list post-list--numbered">
          {items.map((b, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: b.html }} />
          ))}
        </ol>
      );
      continue;
    }

    // Single blocks
    nodes.push(<BlockNode key={i} block={block} />);
    i++;
  }

  return nodes;
}

const BlockNode: React.FC<{ block: BlogBlock }> = ({ block }) => {
  switch (block.type) {
    case 'heading_1':
      return <h2 className="post-h1" dangerouslySetInnerHTML={{ __html: block.html }} />;
    case 'heading_2':
      return <h3 className="post-h2" dangerouslySetInnerHTML={{ __html: block.html }} />;
    case 'heading_3':
      return <h4 className="post-h3" dangerouslySetInnerHTML={{ __html: block.html }} />;
    case 'paragraph':
      return block.html
        ? <p className="post-p" dangerouslySetInnerHTML={{ __html: block.html }} />
        : <div className="post-spacer" />;
    case 'quote':
      return <blockquote className="post-quote" dangerouslySetInnerHTML={{ __html: block.html }} />;
    case 'callout':
      return <div className="post-callout" dangerouslySetInnerHTML={{ __html: block.html }} />;
    case 'divider':
      return <hr className="post-divider" />;
    case 'image':
      return (
        <figure className="post-figure">
          <img src={block.url} alt={block.caption || ''} loading="lazy" />
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      );
    default:
      return null;
  }
};

// ── Main page component ───────────────────────────────────────────────
const BlogPostDetail: React.FC = () => {
  const { slug }                  = useParams<{ slug: string }>();
  const [post, setPost]           = useState<BlogPost | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);

    fetch(`${API_BASE}/api/blog/posts/${slug}`)
      .then(r => {
        if (r.status === 404) throw new Error('not_found');
        if (!r.ok) throw new Error(`API error ${r.status}`);
        return r.json() as Promise<BlogPost>;
      })
      .then(data => { setPost(data); setLoading(false); })
      .catch(err => {
        setError(err.message === 'not_found'
          ? 'Post not found.'
          : 'Unable to load this post. Please try again shortly.');
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="post-page">
        <div className="post-state">
          <div className="post-spinner" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="post-page">
        <div className="post-state post-state--error">
          <p>{error ?? 'Post not found.'}</p>
          <Link to="/blog" className="btn btn-brand-navy mt-4">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  const hasImage = post.coverImageUrl && post.coverImageUrl.trim() !== '';

  return (
    <div className="post-page">

      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="post-hero section--dark">
        <div className="container-xl">
          <Link to="/blog" className="post-back">← Back to Blog</Link>
          {post.category && <span className="section-label">{post.category}</span>}
          <h1 className="post-title">{post.title}</h1>

          {/* Meta row */}
          <div className="post-meta">
            {post.publishDate && <span>{formatDate(post.publishDate)}</span>}
            {post.location    && <span>📍 {post.location}</span>}
            {post.clientType  && <span className="tag tag--red-border">{post.clientType}</span>}
          </div>

          {/* Service chips */}
          {post.services.length > 0 && (
            <div className="post-chips">
              {post.services.map(s => (
                <span className="svc-chip svc-chip--dark" key={s}>{s}</span>
              ))}
            </div>
          )}
        </div>
      </section>
      <div className="red-bar" />

      {/* ── Cover image ────────────────────────────────────────── */}
      {hasImage && (
        <div className="post-cover">
          <div className="container-xl">
            <img src={post.coverImageUrl} alt={post.title} />
          </div>
        </div>
      )}

      {/* ── Body ──────────────────────────────────────────────── */}
      <section className="section">
        <div className="container-xl">
          <div className="post-body">

            {/* Summary lead */}
            {post.summary && (
              <p className="post-lead">{post.summary}</p>
            )}

            {/* Notion page blocks */}
            {post.body.length > 0
              ? renderBlocks(post.body)
              : <p className="post-p" style={{ color: 'var(--gray)' }}>Full post content coming soon.</p>
            }

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="post-tags">
                {post.tags.map(t => <span className="blog-tag" key={t}>#{t}</span>)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────── */}
      <section className="section--dark" style={{ padding: 'clamp(3rem,6vw,5rem) 0', borderTop: '3px solid var(--red)' }}>
        <div className="container-xl">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-4">
            <div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'clamp(1.6rem,3vw,2.5rem)', textTransform: 'uppercase', color: 'var(--white)', marginBottom: '0.5rem' }}>
                Need This for Your Property?
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, fontWeight: 300, margin: 0 }}>
                Free assessment. Quote within 24 hours. No obligation.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-3">
              <Link to="/contact" className="btn btn-brand-primary">Get a Free Quote</Link>
              <a href="tel:3464194499" className="btn btn-brand-outline-white">(346) 419-4499</a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default BlogPostDetail;
